import {
  useGetOrderQuery,
  useGetTypesOfServicesQuery,
  useGetUnitMeasuresQuery,
} from 'redux/Api/ordersApi/ordersApi';
import styles from './OrderCondition.module.scss';
import { setInfoOrderDetail } from 'redux/ordersDetails/slice';
import { useDispatch } from 'react-redux';
import { ReactComponent as InfoIcon } from './img/iconInfo.svg';
import { useEffect, useState } from 'react';
import { LoadingSkeleton } from 'components/LoadingSkeleton';
import { Group, Switch } from '@mantine/core';
import moment from 'moment';
import { formatOrderAddress } from 'utils/strActions';
import { addSpaceNumber } from 'utils/addSpaceNumber';
import {
  CreateOrderFormErrors,
  HandleFieldKey,
  HandleFieldValue,
} from 'components/CreateOrderForm/types';
import { CreateOrderForm } from 'components/CreateOrderForm';
import { TextWithTooltip } from 'hooks/TextWithTooltip';
import { AppTooltip } from 'ui/AppTooltip';
import { useParams } from 'react-router-dom';
import { TIMEZONES } from 'constants/timezones';

export const OrderCondition = ({
  errors,
  clearErrorForField,
  clearError,
}: {
  errors: CreateOrderFormErrors;
  clearErrorForField?: (key: HandleFieldKey) => void;
  clearError?: (field: keyof CreateOrderFormErrors) => void;
}) => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { data, isLoading } = useGetOrderQuery({ id }, { skip: !id });
  const [isExpanded, setIsExpanded] = useState(false);
  const { data: dataTypes } = useGetTypesOfServicesQuery();
  const { data: dataUnits } = useGetUnitMeasuresQuery();

  const handleFieldChange = (key: HandleFieldKey, value: HandleFieldValue) => {
    dispatch(setInfoOrderDetail({ key, value }));
  };

  const isDraft = data?.status?.value === 'order_draft';

  useEffect(() => {
    if (isDraft) {
      handleFieldChange('description', data.order_data.description || '');
      handleFieldChange('type_description', data.order_data.type_description || '');
      handleFieldChange('amount', data.order_data.amount || '');

      handleFieldChange('accept_automatically', data?.accept_automatically || 0);
      handleFieldChange('use_fast_withdraw', data?.use_fast_withdraw || 0);

      if (data.order_data.order_type && dataTypes?.items) {
        const matchingType = dataTypes.items.find(
          (item: any) => item.name === data.order_data.order_type,
        );
        handleFieldChange('order_type', data.order_data.order_type);
        if (matchingType) {
          handleFieldChange('order_type_id', matchingType.id);
        }
      }

      if (data?.is_remote) {
        handleFieldChange('with_address', false);
        handleFieldChange('is_remote', 'Удаленно');
        handleFieldChange('apartment', '');
        handleFieldChange('city', '');
        handleFieldChange('street', '');
        handleFieldChange('home', '');
      } else {
        const city = (data.order_data.city || '').trim();
        const street = (data.order_data.street || '').trim();
        const home = (data.order_data.home || '').trim();
        const hasAddress = city.length > 0 || street.length > 0 || home.length > 0;

        if (hasAddress) {
          handleFieldChange('with_address', true);
          handleFieldChange('is_remote', 'По адресу');
          handleFieldChange('apartment', data.order_data.apartment || '');
          handleFieldChange('city', data.order_data.city || '');
          handleFieldChange('street', data.order_data.street || '');
          handleFieldChange('home', data.order_data.home || '');
        } else {
          handleFieldChange('with_address', false);
          handleFieldChange('is_remote', 'Удаленно');
          handleFieldChange('apartment', '');
          handleFieldChange('city', '');
          handleFieldChange('street', '');
          handleFieldChange('home', '');
        }
      }

      if (data?.preset) {
        handleFieldChange('order_preset_id', data.preset.id);
      }
      handleFieldChange('add_tax_amount', data?.include_tax_amount === 1 ? true : false);
      if (data?.order_data?.date_end) {
        handleFieldChange('with_date_end', true);
        handleFieldChange('date_end', data.order_data.date_end);
      }
      if (data?.order_data?.date_start) {
        handleFieldChange('with_date_start', true);
        handleFieldChange('date_start', data.order_data.date_start);
      }
    }
  }, [data, dataTypes, isDraft]);

  const renderDescription = (description: string) => {
    const wordCount = description.trim().split(/\s+/).length;
    const isLongDescription = wordCount > 100;

    if (!isLongDescription) {
      return <p className={styles.order_inner__value}>{description}</p>;
    }

    const words = description.trim().split(/\s+/);
    const displayedText = isExpanded
      ? description
      : words.slice(0, 100).join(' ') + '...';

    return (
      <div className={styles.description_container}>
        <p className={styles.order_inner__value}>{displayedText}</p>
        <button
          className={styles.spoiler_button}
          onClick={() => setIsExpanded(!isExpanded)}>
          {isExpanded ? 'Свернуть описание' : 'Развернуть описание'}
        </button>
      </div>
    );
  };

  if (isLoading) return <LoadingSkeleton />;

  const orderAddress = formatOrderAddress(data?.order_data) || data?.order_data?.address;

  const measure = data?.order_data?.unit_measure;

  const tooltipVolume = (orderData: any) => {
    return (
      <>{`Кол-во услуг: ${orderData?.unit_volume}, по ${addSpaceNumber(
        String(orderData?.unit_price),
      )} ₽ за ед.`}</>
    );
  };

  return isDraft ? (
    <CreateOrderForm
      handleFieldChange={handleFieldChange}
      errors={errors}
      clearErrorForField={clearErrorForField}
      clearError={clearError}
    />
  ) : (
    <div className={styles.order}>
      <div className={styles.order_inner}>
        <p className={styles.order_inner__header}>Условия заказа</p>

        {data?.preset && (
          <div className={styles.order_inner__block}>
            <p className={styles.order_inner__title}>Шаблон</p>
            <span className={styles.order_inner__template}>{data?.preset?.name}</span>
          </div>
        )}

        <div className={styles.order_inner__block}>
          <p className={styles.order_inner__title}>Номер заказа</p>
          <span className={styles.order_inner__value}>{data?.number}</span>
        </div>

        <div className={styles.order_inner__block}>
          <p className={styles.order_inner__title}>Описание</p>
          {renderDescription(data?.order_data?.type_description || '')}
          {renderDescription(data?.order_data?.description || '')}
        </div>

        <div className={styles.order_inner__block}>
          <p className={styles.order_inner__title}>Категория услуг</p>
          <p className={styles.order_inner__value}>{data?.order_data?.order_type}</p>
        </div>

        <div className={styles.order_inner__block}>
          <p className={styles.order_inner__title}>Адрес</p>
          <p className={styles.order_inner__value}>
            {!data?.is_remote ? orderAddress : 'Удаленная работа'}
          </p>
        </div>

        <div className={styles.order_inner__block}>
          <p className={styles.order_inner__title}>Часовой пояс</p>
          <p className={styles.order_inner__value}>
            {TIMEZONES.find((item) => item.value === data?.order_data?.timezone)?.label}
          </p>
        </div>

        <div className={styles.date}>
          <div className={styles.date_details}>
            <p>Начало заказа</p>
            <div>
              {data?.order_data?.date_start
                ? `${moment(data.order_data.date_start).format('DD.MM.YYYY, HH:mm')}`
                : 'Не указано'}
            </div>
          </div>
          <div className={styles.date_details}>
            <p>Завершение заказа</p>
            <div>
              {data?.order_data?.date_end
                ? `${moment(data.order_data.date_end).format('DD.MM.YYYY, HH:mm')}`
                : 'Не указано'}
            </div>
          </div>
        </div>

        <div className={styles.order_inner__block}>
          <p className={styles.order_inner__title}>
            {data?.order_data?.unit_volume}{' '}
            {dataUnits?.[measure as keyof typeof dataUnits]}
          </p>
          <span className={styles.order_inner__value}>
            {addSpaceNumber(String(data?.order_data.unit_price))} ₽
          </span>
        </div>

        {!data?.order_data?.is_unit_based ? (
          <Group gap='8px'>
            <Switch
              color='#002CFB'
              styles={{
                label: {
                  fontSize: '16px',
                  lineHeight: '22px',
                  color: '#121922',
                  cursor: 'default',
                },
                track: {
                  border: 'none',
                  cursor: 'not-allowed',
                  backgroundColor:
                    data?.include_tax_amount === 1
                      ? '#e1e6fe'
                      : 'rgba(173, 191, 223, 0.40)',
                },
                thumb: {
                  border: 'none',
                },
              }}
              checked={data?.include_tax_amount === 1}
              disabled
              label='Добавить 6%'
            />
            <AppTooltip
              label={
                data?.order_data?.unit_price
                  ? `Мы добавим такую сумму, которая после выплаты 6% налога, составит ${data?.order_data?.unit_price} ₽`
                  : `Мы добавим такую сумму, которая после выплаты 6% налога составит введенную`
              }>
              <InfoIcon style={{ cursor: 'pointer' }} />
            </AppTooltip>
          </Group>
        ) : null}

        {data?.order_data?.is_unit_based ? (
          <>
            <Group gap='8px' h='24px'>
              <Switch
                color='#002CFB'
                styles={{
                  track: {
                    border: 'none',
                    backgroundColor:
                      data?.order_data?.is_unit_based === 1
                        ? '#e1e6fe'
                        : 'rgba(173, 191, 223, 0.40)',
                  },
                  thumb: {
                    border: 'none',
                  },
                }}
                checked={data?.order_data?.is_unit_based === 1}
                disabled
              />
              <TextWithTooltip
                isMobile
                className={styles.switchRowText}
                content='Оплата за выполненный объем'
              />
              <AppTooltip label='Укажи цену за единицу. Итоговая сумма будет рассчитываться при завершении заказа на основе выполненного объема'>
                <InfoIcon style={{ cursor: 'pointer' }} />
              </AppTooltip>
            </Group>
            <div className={styles.volumeRow}>
              <div className={styles.volumeField}>
                <p className={styles.volumeLabel}>
                  <span className={styles.volumeLabel_full}>Минимальный объем</span>
                  <span className={styles.volumeLabel_short}>Мин. объем</span>
                </p>

                <span style={{ height: '24px' }}>
                  {data?.order_data?.unit_volume_min}
                </span>
              </div>
              <div className={styles.volumeField}>
                <p className={styles.volumeLabel}>
                  <span className={styles.volumeLabel_full}>Максимальный объем</span>
                  <span className={styles.volumeLabel_short}>Макс. объем</span>
                </p>
                <span style={{ height: '24px' }}>
                  {data?.order_data?.unit_volume_max || 'Не указано'}
                </span>
              </div>
            </div>
            <div className={styles.order_inner__amount}>
              <p>
                {addSpaceNumber(String(data?.order_data.unit_price))} ₽/
                {dataUnits?.[measure as keyof typeof dataUnits]}
              </p>
            </div>
          </>
        ) : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div className={styles.order_inner__amount}>
                <p>{addSpaceNumber(String(data?.order_data.amount))} ₽</p>
                <AppTooltip label={tooltipVolume(data?.order_data)}>
                  <InfoIcon style={{ cursor: 'pointer' }} />
                </AppTooltip>
              </div>
              <p>
                Вознаграждение оператора{' '}
                {addSpaceNumber(String(data?.order_data.commission_amount))} ₽
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
